import { Resolution, VijiCoreError } from '../types/index.js';

/**
 * Manages the secure IFrame that contains the canvas for scene rendering.
 * Provides complete isolation while allowing controlled canvas access.
 * Enhanced with Phase 7 interaction support.
 */
export class IFrameManager {
  private iframe: HTMLIFrameElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private offscreenCanvas: OffscreenCanvas | null = null;
  private isInitialized = false;
  private scale: number = 1.0;
  
  // Debug logging control
  private debugMode = false;

  /**
   * Enable or disable debug logging
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
  }

  /**
   * Debug logging helper
   */
  private debugLog(message: string, ...args: any[]): void {
    if (this.debugMode) {
      console.log(message, ...args);
    }
  }

  // Phase 7: Interaction event listeners
  private interactionListeners = new Map<string, (data: any) => void>();
  private isInteractionEnabled = true;

  // Mouse canvas tracking
  private isMouseInCanvas = false;

  // Touch tracking for gesture recognition
  private activeTouchIds = new Set<number>();

  constructor(
    private hostContainer: HTMLElement,
  ) {}

  /**
   * Creates a secure IFrame with proper sandbox attributes
   */
  async createSecureIFrame(): Promise<HTMLIFrameElement> {
    try {
      // Create IFrame with security sandbox
      const iframe = document.createElement('iframe');
      
      // Security: Sandbox with controlled permissions
      // Note: allow-same-origin is required for canvas access but limits sandboxing
      // In future phases, consider message-passing for better security
      iframe.sandbox.add('allow-scripts');
      iframe.sandbox.add('allow-same-origin'); // Required for canvas/WebGL access
      
      // Styling: Full container size
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframe.style.border = 'none';
      iframe.style.display = 'block';
      
      // Generate secure IFrame content
      const iframeContent = this.generateIFrameHTML();
      const blob = new Blob([iframeContent], { type: 'text/html' });
      iframe.src = URL.createObjectURL(blob);
      
      // Mount to container
      this.hostContainer.appendChild(iframe);
      
      // Wait for IFrame to load and document to be ready
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new VijiCoreError('IFrame load timeout', 'IFRAME_TIMEOUT'));
        }, 5000);
        
        const checkReady = () => {
          if (iframe.contentDocument && iframe.contentDocument.readyState === 'complete') {
            clearTimeout(timeout);
            resolve();
          }
        };
        
        iframe.onload = () => {
          // Double-check document is ready
          if (iframe.contentDocument?.readyState === 'complete') {
            clearTimeout(timeout);
            resolve();
          } else {
            // Wait a bit more for document to be ready
            iframe.contentDocument?.addEventListener('DOMContentLoaded', checkReady);
            // Fallback check
            setTimeout(checkReady, 100);
          }
        };
        
        iframe.onerror = () => {
          clearTimeout(timeout);
          reject(new VijiCoreError('IFrame load failed', 'IFRAME_LOAD_ERROR'));
        };
      });
      
      this.iframe = iframe;
      return iframe;
    } catch (error) {
      throw new VijiCoreError(
        `Failed to create secure IFrame: ${error}`,
        'IFRAME_CREATION_ERROR',
        { error }
      );
    }
  }

  /**
   * Creates canvas inside the IFrame and returns OffscreenCanvas for WebWorker
   */
  async createCanvas(): Promise<OffscreenCanvas> {
    if (!this.iframe?.contentWindow) {
      throw new VijiCoreError('IFrame not ready for canvas creation', 'IFRAME_NOT_READY');
    }

    try {
      // Wait for iframe document to be accessible with retry logic
      const iframeDoc = await this.waitForIFrameDocument();
      
      // Create canvas in IFrame
      const canvas = iframeDoc.createElement('canvas');
      canvas.id = 'viji-canvas';
      this.canvas = canvas;
      
      // Set canvas size based on resolution
      const { width, height } = this.calculateCanvasSize();
      canvas.width = width * this.scale;
      canvas.height = height * this.scale;
      
      // Styling: Full container size
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.display = 'block';
      
      // Add to IFrame body
      const body = iframeDoc.querySelector('body');
      if (!body) {
        throw new VijiCoreError('IFrame body not found', 'IFRAME_BODY_ERROR');
      }
      
      body.appendChild(canvas);
      
      // Phase 7: Set up interaction event listeners
      this.setupInteractionListeners(canvas, iframeDoc);
      
      // Create OffscreenCanvas for WebWorker
      const offscreenCanvas = canvas.transferControlToOffscreen();
      this.offscreenCanvas = offscreenCanvas;
      this.isInitialized = true;
      
      return offscreenCanvas;
    } catch (error) {
      throw new VijiCoreError(
        `Failed to create canvas: ${error}`,
        'CANVAS_CREATION_ERROR',
        { error }
      );
    }
  }

  /**
   * Waits for iframe document to be accessible with retry logic
   */
  private async waitForIFrameDocument(): Promise<Document> {
    const maxRetries = 15;
    const retryDelay = 150;
    
    for (let i = 0; i < maxRetries; i++) {
      try {
        const iframeDoc = this.iframe?.contentDocument;
        
        // Check multiple readiness indicators
        if (iframeDoc && 
            (iframeDoc.readyState === 'complete' || iframeDoc.readyState === 'interactive') &&
            iframeDoc.body &&
            this.iframe?.contentWindow) {
          
          this.debugLog(`IFrame document ready after ${i + 1} attempts`);
          return iframeDoc;
        }
        
        this.debugLog(`IFrame not ready attempt ${i + 1}/${maxRetries}:`, {
          hasDocument: !!iframeDoc,
          readyState: iframeDoc?.readyState,
          hasBody: !!iframeDoc?.body,
          hasWindow: !!this.iframe?.contentWindow
        });
        
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      } catch (error) {
        this.debugLog(`IFrame access error attempt ${i + 1}/${maxRetries}:`, error);
        // Security or access error - wait and retry
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
    
    throw new VijiCoreError('Cannot access IFrame document after retries', 'IFRAME_ACCESS_ERROR');
  }

  /**
   * Updates canvas resolution setting (scaling handled in worker)
   */
  updateScale(scale: number): { width: number; height: number } {
    this.scale = scale;
    return this.getEffectiveResolution();
  }

  /**
  * Gets current scale
  */
  getScale(): number {
    return this.scale;
  }

  /**
   * Destroys the IFrame and cleans up resources
   */
  destroy(): void {
    try {
      if (this.iframe) {
        // Revoke blob URL to prevent memory leaks
        if (this.iframe.src.startsWith('blob:')) {
          URL.revokeObjectURL(this.iframe.src);
        }
        
        // Remove from DOM
        this.iframe.remove();
        this.iframe = null;
      }
      
      this.offscreenCanvas = null;
      this.isInitialized = false;
    } catch (error) {
      console.warn('Error during IFrame cleanup:', error);
    }
  }

  /**
   * Checks if IFrame is ready for use
   */
  get ready(): boolean {
    return this.isInitialized && 
           this.iframe !== null && 
           this.offscreenCanvas !== null;
  }

  /**
   * Gets the IFrame element
   */
  get element(): HTMLIFrameElement | null {
    return this.iframe;
  }

  /**
   * Generates the HTML content for the secure IFrame
   */
  private generateIFrameHTML(): string {
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Viji Scene Container</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    html, body {
      width: 100%;
      height: 100%;
      overflow: hidden;
      background: #000;
    }
    
    canvas {
      display: block;
      width: 100%;
      height: 100%;
      image-rendering: auto;
    }
  </style>
</head>
<body>
  <!-- Canvas will be created dynamically -->
</body>
</html>
    `.trim();
  }

  /**
   * Calculates canvas dimensions based on container size
   * Canvas internal size starts as container size, then worker updates it based on resolution
   */
  private calculateCanvasSize(): { width: number; height: number } {
    // Use full container size for initial canvas resolution
    // Worker will update canvas.width/height based on effective resolution messages
    const containerRect = this.hostContainer.getBoundingClientRect();
    
    return {
      width: Math.round(containerRect.width),
      height: Math.round(containerRect.height)
    };
  }

  /**
   * Gets the effective resolution that should be used for rendering
   */
  getEffectiveResolution(): Resolution {
    const containerRect = this.hostContainer.getBoundingClientRect();
    const containerWidth = Math.round(containerRect.width);
    const containerHeight = Math.round(containerRect.height);

    const scale = Math.max(0.1, Math.min(1.0, this.scale));
    return {
      width: Math.round(containerWidth * scale),
      height: Math.round(containerHeight * scale),
    };
  }

  // Phase 7: Interaction Management Methods

  /**
   * Sets up interaction event listeners on the canvas and document
   */
  private setupInteractionListeners(canvas: HTMLCanvasElement, iframeDoc: Document): void {
    if (!this.isInteractionEnabled) return;

    // Make canvas focusable for keyboard events
    canvas.tabIndex = 0;
    canvas.style.outline = 'none';

    // Mouse events
    canvas.addEventListener('mousedown', this.handleMouseEvent.bind(this), { passive: false });
    canvas.addEventListener('mousemove', this.handleMouseEvent.bind(this), { passive: false });
    canvas.addEventListener('mouseup', this.handleMouseEvent.bind(this), { passive: false });
    canvas.addEventListener('mouseenter', this.handleMouseEnter.bind(this), { passive: false });
    canvas.addEventListener('mouseleave', this.handleMouseLeave.bind(this), { passive: false });
    canvas.addEventListener('wheel', this.handleWheelEvent.bind(this), { passive: false });
    canvas.addEventListener('contextmenu', (e) => e.preventDefault()); // Disable right-click menu

    // Keyboard events (on document to capture all keys)
    iframeDoc.addEventListener('keydown', this.handleKeyboardEvent.bind(this), { passive: false });
    iframeDoc.addEventListener('keyup', this.handleKeyboardEvent.bind(this), { passive: false });

    // Touch events
    canvas.addEventListener('touchstart', this.handleTouchEvent.bind(this), { passive: false });
    canvas.addEventListener('touchmove', this.handleTouchEvent.bind(this), { passive: false });
    canvas.addEventListener('touchend', this.handleTouchEvent.bind(this), { passive: false });
    canvas.addEventListener('touchcancel', this.handleTouchEvent.bind(this), { passive: false });

    // Focus management
    canvas.addEventListener('mousedown', () => canvas.focus());
    canvas.addEventListener('touchstart', () => canvas.focus());
  }

  /**
   * Handles mouse events and transforms coordinates
   */
  private handleMouseEvent(event: MouseEvent): void {
    if (!this.canvas || !this.isInteractionEnabled) return;

    event.preventDefault();

    const rect = this.canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left) * (this.canvas.width / rect.width);
    const y = (event.clientY - rect.top) * (this.canvas.height / rect.height);

    // Calculate movement deltas
    const deltaX = event.movementX || 0;
    const deltaY = event.movementY || 0;

    const data = {
      x,
      y,
      buttons: event.buttons,
      deltaX,
      deltaY,
      wheelDeltaX: 0,
      wheelDeltaY: 0,
      isInCanvas: this.isMouseInCanvas,
      timestamp: performance.now()
    };

    this.emitInteractionEvent('mouse-update', data);
  }

  /**
   * Handles mouse enter events
   */
  private handleMouseEnter(event: MouseEvent): void {
    if (!this.isInteractionEnabled) return;
    
    this.isMouseInCanvas = true;
    
    // Trigger a mouse update to immediately reflect the isInCanvas state
    this.handleMouseEvent(event);
  }

  /**
   * Handles mouse leave events
   */
  private handleMouseLeave(event: MouseEvent): void {
    if (!this.isInteractionEnabled) return;
    
    this.isMouseInCanvas = false;
    
    // Trigger a mouse update to immediately reflect the isInCanvas state
    this.handleMouseEvent(event);
  }

  /**
   * Handles wheel events
   */
  private handleWheelEvent(event: WheelEvent): void {
    if (!this.canvas || !this.isInteractionEnabled) return;

    event.preventDefault();

    const rect = this.canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left) * (this.canvas.width / rect.width);
    const y = (event.clientY - rect.top) * (this.canvas.height / rect.height);

    const data = {
      x,
      y,
      buttons: event.buttons,
      deltaX: 0,
      deltaY: 0,
      wheelDeltaX: event.deltaX,
      wheelDeltaY: event.deltaY,
      timestamp: performance.now()
    };

    this.emitInteractionEvent('mouse-update', data);
  }

  /**
   * Handles keyboard events
   */
  private handleKeyboardEvent(event: KeyboardEvent): void {
    if (!this.isInteractionEnabled) return;

    // Allow some default behaviors for accessibility/usability
    const allowedKeys = ['Tab', 'F1', 'F2', 'F3', 'F4', 'F5', 'F11', 'F12'];
    if (!allowedKeys.includes(event.key)) {
      event.preventDefault();
    }

    const data = {
      type: event.type as 'keydown' | 'keyup',
      key: event.key,
      code: event.code,
      shiftKey: event.shiftKey,
      ctrlKey: event.ctrlKey,
      altKey: event.altKey,
      metaKey: event.metaKey,
      timestamp: performance.now()
    };

    this.emitInteractionEvent('keyboard-update', data);
  }

  /**
   * Handles touch events and tracks multi-touch
   */
  private handleTouchEvent(event: TouchEvent): void {
    if (!this.canvas || !this.isInteractionEnabled) return;

    event.preventDefault();

    const rect = this.canvas.getBoundingClientRect();
    const scaleX = this.canvas.width / rect.width;
    const scaleY = this.canvas.height / rect.height;

    // Convert TouchList to array with transformed coordinates
    const touches = Array.from(event.touches).map(touch => ({
      identifier: touch.identifier,
      clientX: (touch.clientX - rect.left) * scaleX,
      clientY: (touch.clientY - rect.top) * scaleY,
      pressure: (touch as any).pressure || 0,
      radiusX: (touch as any).radiusX || 10,
      radiusY: (touch as any).radiusY || 10,
      rotationAngle: (touch as any).rotationAngle || 0,
      force: (touch as any).force || (touch as any).pressure || 0
    }));

    // Track active touch IDs for gesture recognition
    if (event.type === 'touchstart') {
      for (const touch of event.changedTouches) {
        this.activeTouchIds.add(touch.identifier);
      }
    } else if (event.type === 'touchend' || event.type === 'touchcancel') {
      for (const touch of event.changedTouches) {
        this.activeTouchIds.delete(touch.identifier);
      }
    }

    const data = {
      type: event.type as 'touchstart' | 'touchmove' | 'touchend' | 'touchcancel',
      touches,
      timestamp: performance.now()
    };

    this.emitInteractionEvent('touch-update', data);
  }

  /**
   * Emits an interaction event to registered listeners
   */
  private emitInteractionEvent(eventType: string, data: any): void {
    const listener = this.interactionListeners.get(eventType);
    if (listener) {
      listener(data);
    }
  }

  /**
   * Registers an interaction event listener
   */
  onInteractionEvent(eventType: string, listener: (data: any) => void): void {
    this.interactionListeners.set(eventType, listener);
  }

  /**
   * Removes an interaction event listener
   */
  offInteractionEvent(eventType: string): void {
    this.interactionListeners.delete(eventType);
  }

  /**
   * Enables or disables interaction event capture
   */
  setInteractionEnabled(enabled: boolean): void {
    this.isInteractionEnabled = enabled;
  }

  /**
   * Gets the canvas element (for coordinate calculations)
   */
  getCanvas(): HTMLCanvasElement | null {
    return this.canvas;
  }
} 